import React from 'react';

import type { UseFormClearErrors } from 'react-hook-form';

import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import LabelWithStatus from '@/components/ui/forms/LabelWithStatus';
import Select from '@/components/ui/forms/Select';
import TextArea from '@/components/ui/forms/TextArea';
import TimeInputs from '@/components/ui/forms/TimeInputs';
import { type UseReactHookFormReturn } from '@/hooks/common/useReactHookForm';

import { contactTypeReasonsV2, reasonOptions } from '../constants';
import { type ContactType, type FormValues, type Value } from '../types';

import styles from './AbsencesFormInputsOnOrAfterReason.module.scss';

interface Props {
  registerWithInnerRef: UseReactHookFormReturn<FormValues>['registerWithInnerRef'];
  validFields: UseReactHookFormReturn<FormValues>['formState']['validFields'];
  dirtyFields: UseReactHookFormReturn<FormValues>['formState']['dirtyFields'];
  contactType: Value;
  errorMessages: UseReactHookFormReturn<FormValues>['formState']['errorMessages'];
  clearErrors: UseFormClearErrors<FormValues>;
}

const AbsencesFormInputsOnOrAfterReason: React.FC<Props> = ({
  registerWithInnerRef,
  validFields,
  dirtyFields,
  contactType,
  errorMessages,
  clearErrors,
}) => {
  const [reason, setReason] = React.useState<Value>(null);

  return (
    <>
      <FormGroup>
        <LabelWithStatus required valid={validFields.reason}>
          理由
        </LabelWithStatus>
        <Field>
          <Select
            data-testid="reason"
            inputProps={registerWithInnerRef('reason')}
            placeholder="選択してください"
            options={
              contactType
                ? reasonOptions.filter((opt) =>
                    contactTypeReasonsV2[contactType as ContactType].includes(
                      opt.value,
                    ),
                  )
                : []
            }
            onChange={(val) => {
              setReason(val);
              if (!dirtyFields.detail_comment) {
                clearErrors('detail_comment');
              }
            }}
          />
          <ErrorList errors={errorMessages.reason} />
        </Field>
      </FormGroup>

      <FormGroup hidden={contactType !== 'tardy'}>
        <LabelWithStatus required valid={validFields.go_to_school_at}>
          登校時刻
        </LabelWithStatus>
        <Field>
          <TimeInputs inputProps={registerWithInnerRef('go_to_school_at')} />
          <ErrorList errors={errorMessages.go_to_school_at} />
        </Field>
      </FormGroup>

      <FormGroup hidden={contactType !== 'early_leaving'}>
        <LabelWithStatus required valid={validFields.leave_school_at}>
          下校時刻
        </LabelWithStatus>
        <Field>
          <TimeInputs inputProps={registerWithInnerRef('leave_school_at')} />
          <ErrorList errors={errorMessages.leave_school_at} />
        </Field>
      </FormGroup>

      <FormGroup
        hidden={
          !reason ||
          !['poor_health', 'injury', 'visit_hospital'].includes(reason)
        }
      >
        <LabelWithStatus
          valid={validFields.disease_name && dirtyFields.disease_name}
        >
          病名
        </LabelWithStatus>
        <Field>
          <Input
            {...registerWithInnerRef('disease_name')}
            placeholder="例）インフルエンザ、骨折など"
          />
          <ErrorList errors={errorMessages.disease_name} />
        </Field>
      </FormGroup>

      <FormGroup
        hidden={
          !reason ||
          !['poor_health', 'injury', 'visit_hospital'].includes(reason)
        }
      >
        <LabelWithStatus required valid={validFields.symptom}>
          症状
        </LabelWithStatus>
        <Field>
          <Input
            {...registerWithInnerRef('symptom')}
            placeholder="例）頭痛、発熱、咳、腹痛、発疹など"
          />
          <ErrorList errors={errorMessages.symptom} />
        </Field>
      </FormGroup>

      <FormGroup
        hidden={
          !reason ||
          !['poor_health', 'injury', 'visit_hospital'].includes(reason)
        }
      >
        <LabelWithStatus
          valid={validFields.body_temperature && dirtyFields.body_temperature}
        >
          体温（°C）
        </LabelWithStatus>
        <Field>
          <Input
            {...registerWithInnerRef('body_temperature')}
            placeholder="例）36.5"
          />
          <ErrorList errors={errorMessages.body_temperature} />
        </Field>
      </FormGroup>

      <FormGroup
        hidden={
          !reason ||
          !['poor_health', 'injury', 'visit_hospital'].includes(reason)
        }
      >
        <LabelWithStatus
          valid={
            validFields.doctors_instruction && dirtyFields.doctors_instruction
          }
        >
          医師からの出席停止指示
        </LabelWithStatus>
        <Field>
          <Select
            inputProps={registerWithInnerRef('doctors_instruction')}
            placeholder="選択してください"
            options={[
              { name: 'あり', value: '1' },
              { name: 'なし', value: '0' },
            ]}
          />
          <ErrorList errors={errorMessages.doctors_instruction} />
        </Field>
      </FormGroup>

      <FormGroup hidden={!reason || reason !== 'mourning'}>
        <LabelWithStatus required valid={validFields.relationship}>
          亡くなった方と生徒の関係
        </LabelWithStatus>
        <Field>
          <Input
            {...registerWithInnerRef('relationship')}
            placeholder="例）祖父"
          />
          <ErrorList errors={errorMessages.relationship} />
        </Field>
      </FormGroup>

      <FormGroup
        hidden={
          !reason || (reason !== 'traffic_delay' && reason !== 'traffic_stop')
        }
      >
        <LabelWithStatus required valid={validFields.transportation}>
          交通機関
        </LabelWithStatus>
        <Field>
          <Input
            {...registerWithInnerRef('transportation')}
            placeholder="例）電車、バスなど"
          />
          <ErrorList errors={errorMessages.transportation} />
        </Field>
      </FormGroup>

      <FormGroup>
        <LabelWithStatus
          required={reason === 'family' || reason === 'others'}
          valid={validFields.detail_comment && dirtyFields.detail_comment}
        >
          詳細コメント
        </LabelWithStatus>
        <Field isFullWidth>
          <TextArea
            {...registerWithInnerRef('detail_comment')}
            rows={3}
            placeholder="詳細を入力してください"
            className={styles.textArea}
          />
          <ErrorList errors={errorMessages.detail_comment} />
          <p className={styles.notice}>
            ご質問など返答が必要な内容につきましては「詳細コメント」には記入せず、別途学校に問い合わせてください。
          </p>
        </Field>
      </FormGroup>
    </>
  );
};

export default AbsencesFormInputsOnOrAfterReason;
